import React, { useEffect, useMemo, useRef, useState } from "react";
import { Center, Group, Loader, Paper, Stack, Text, UnstyledButton, useMantineTheme } from "@mantine/core";
import { PieChart } from "@mui/x-charts/PieChart";
import { collection, collectionGroup, getCountFromServer, query, Timestamp, where } from "firebase/firestore";
import { format } from "date-fns";
import { useFirestore } from "../helpers/firebaseContext";
import useFirestoreCollection from "../helpers/useFirestoreCollection";
import { Fund, OrderStatusCode } from "../types";
import type { OrdersSummaryFilterSelection } from "./orders_instant_search";

type StatusSlice = {
    code: string;
    label: string;
    value: number;
    color: string;
};

type FundPeriodSummary = {
    fundId: string;
    nextExpectedNavDate: Date | null;
    slices: StatusSlice[];
    total: number;
    error?: string;
};

type OpenEndedOrderPeriodSummaryProps = {
    refreshToken?: number;
    onSliceSelect?: (selection: OrdersSummaryFilterSelection) => void;
};

type OrderPeriodSummaryContentProps = OpenEndedOrderPeriodSummaryProps & {
    funds: Fund[];
    hideFundName?: boolean;
    panelTitle?: string;
    cardMinHeight?: number;
};

type OpenEndedFundOrderPeriodSummaryProps = {
    fund: Fund;
    refreshToken?: number;
    panelTitle?: string;
    cardMinHeight?: number;
};

const AUTO_REFRESH_INTERVAL_MS = 15000;
const ORDER_STATUS_CODES = Object.values(OrderStatusCode);
const FALLBACK_COLORS = [
    "#C5A548",
    "#335B74",
    "#D84A05",
    "#30C5FF",
    "#545E75",
    "#12B886",
    "#7FB7BE",
    "#CED3DC",
    "#262A10",
    "#550000",
];

const ORDER_STATUS_TAGS: Record<string, { badgeColor: string; title: string }> = {
    [OrderStatusCode.waiting_for_kyc]: { badgeColor: "gray", title: "Waiting for KYC approval (CR)" },
    [OrderStatusCode.need_slip]: { badgeColor: "yellow", title: "Need Slip (RM)" },
    [OrderStatusCode.commitment]: { badgeColor: "pink", title: "Commitment (RM)" },
    [OrderStatusCode.done]: { badgeColor: "green", title: "Done" },
    [OrderStatusCode.approval]: { badgeColor: "orange", title: "Waiting for approval (CR)" },
    [OrderStatusCode.review]: { badgeColor: "cyan", title: "Waiting for review (CR)" },
    [OrderStatusCode.selling_agent_review]: { badgeColor: "teal", title: "Waiting for selling agent review (KFH)" },
    [OrderStatusCode.waiting_for_nav]: { badgeColor: "indigo", title: "Waiting for NAV (Operations)" },
    [OrderStatusCode.need_signature]: { badgeColor: "grape", title: "Need Signature (Client)" },
    [OrderStatusCode.signature_verification]: { badgeColor: "violet", title: "Verify DocuSign Signature (Operations)" },
    [OrderStatusCode.processing]: { badgeColor: "gray", title: "Processing (Operations)" },
    [OrderStatusCode.need_kyc]: { badgeColor: "pink", title: "Need client to submit KYC (Client)" },
    [OrderStatusCode.manual]: { badgeColor: "blue", title: "Manual" },
    [OrderStatusCode.unknown]: { badgeColor: "gray", title: "Unknown" },
    [OrderStatusCode.need_form]: { badgeColor: "blue", title: "Need Form (RM)" },
    [OrderStatusCode.waiting_for_collection_date]: { badgeColor: "lime", title: "Waiting For Collection Date" },
    [OrderStatusCode.waiting_certified_rm]: { badgeColor: "pink", title: "Waiting Licensed RM Review (RM)" },
    [OrderStatusCode.rejected]: { badgeColor: "red", title: "Rejected" },
};

const alignDateToNavDayUtc = (seed: Date, navUpdateDay: number): Date => {
    const aligned = new Date(Date.UTC(seed.getUTCFullYear(), seed.getUTCMonth(), seed.getUTCDate()));
    const diff = (navUpdateDay - aligned.getUTCDay() + 7) % 7;
    aligned.setUTCDate(aligned.getUTCDate() + diff);
    return aligned;
};

const resolveUpcomingNavDate = (fund: Fund): Date | null => {
    if (!(fund.nav_date instanceof Timestamp) || typeof fund.nav_update_day_of_week !== "number") {
        return null;
    }

    const fundNavDate = fund.nav_date.toDate();
    const seed = new Date(Date.UTC(fundNavDate.getUTCFullYear(), fundNavDate.getUTCMonth(), fundNavDate.getUTCDate()));
    seed.setUTCDate(seed.getUTCDate() + 1);
    return alignDateToNavDayUtc(seed, fund.nav_update_day_of_week);
};

const getUtcDayRange = (value: Date) => {
    const start = new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate()));
    const end = new Date(start);
    end.setUTCDate(end.getUTCDate() + 1);
    return {
        start: Timestamp.fromDate(start),
        end: Timestamp.fromDate(end),
    };
};

const fallbackStatusLabel = (code: string) =>
    code
        .replace(/_/g, " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase());

const resolveMantineColor = (
    badgeColor: string | undefined,
    theme: ReturnType<typeof useMantineTheme>,
    index: number,
) => {
    if (badgeColor && badgeColor in theme.colors) {
        const shades = theme.colors[badgeColor];
        return shades?.[6] || shades?.[5] || shades?.[0] || FALLBACK_COLORS[index % FALLBACK_COLORS.length];
    }
    return FALLBACK_COLORS[index % FALLBACK_COLORS.length];
};

const sortFundsByUpcomingNavDate = (funds: Fund[]) =>
    funds
        .slice()
        .sort((left, right) => {
            const leftDate = resolveUpcomingNavDate(left)?.getTime() ?? Number.MAX_SAFE_INTEGER;
            const rightDate = resolveUpcomingNavDate(right)?.getTime() ?? Number.MAX_SAFE_INTEGER;
            if (leftDate !== rightDate) return leftDate - rightDate;
            return (left.short_name || left.name_en || left.id).localeCompare(right.short_name || right.name_en || right.id);
        });

const OrderPeriodSummaryContent = ({
    funds,
    refreshToken = 0,
    onSliceSelect,
    hideFundName = false,
    panelTitle,
    cardMinHeight = 234,
}: OrderPeriodSummaryContentProps) => {
    const theme = useMantineTheme();
    const db = useFirestore();
    const [summaries, setSummaries] = useState<Record<string, FundPeriodSummary>>({});
    const [loading, setLoading] = useState(false);
    const [autoRefreshToken, setAutoRefreshToken] = useState(0);
    const hasLoadedRef = useRef(false);

    useEffect(() => {
        const triggerAutoRefresh = () => {
            if (typeof document !== "undefined" && document.visibilityState !== "visible") {
                return;
            }
            setAutoRefreshToken((current) => current + 1);
        };

        const intervalId = window.setInterval(triggerAutoRefresh, AUTO_REFRESH_INTERVAL_MS);
        window.addEventListener("focus", triggerAutoRefresh);
        document.addEventListener("visibilitychange", triggerAutoRefresh);

        return () => {
            window.clearInterval(intervalId);
            window.removeEventListener("focus", triggerAutoRefresh);
            document.removeEventListener("visibilitychange", triggerAutoRefresh);
        };
    }, []);

    useEffect(() => {
        let cancelled = false;

        const fetchSummaries = async () => {
            if (!funds.length) {
                if (!cancelled) {
                    setSummaries({});
                    setLoading(false);
                }
                return;
            }

            setLoading(!hasLoadedRef.current);
            setSummaries((current) =>
                funds.reduce<Record<string, FundPeriodSummary>>((accumulator, fund) => {
                    accumulator[fund.id] = current[fund.id] ?? {
                        fundId: fund.id,
                        nextExpectedNavDate: resolveUpcomingNavDate(fund),
                        slices: [],
                        total: 0,
                    };
                    return accumulator;
                }, {}),
            );

            const nextSummaries = await Promise.all(
                funds.map(async (fund) => {
                    const nextExpectedNavDate = resolveUpcomingNavDate(fund);
                    if (!nextExpectedNavDate) {
                        return {
                            fundId: fund.id,
                            nextExpectedNavDate: null,
                            slices: [],
                            total: 0,
                            error: "Missing NAV setup",
                        } satisfies FundPeriodSummary;
                    }

                    const { start, end } = getUtcDayRange(nextExpectedNavDate);
                    try {
                        const slices = (await Promise.all(
                            ORDER_STATUS_CODES.map(async (statusCode, index) => {
                                const snapshot = await getCountFromServer(query(
                                    collectionGroup(db, "orders"),
                                    where("fund.id", "==", fund.id),
                                    where("tags.orderStatus.code", "==", statusCode),
                                    where("expected_nav_date", ">=", start),
                                    where("expected_nav_date", "<", end),
                                ));
                                return {
                                    code: statusCode,
                                    label: ORDER_STATUS_TAGS[statusCode]?.title || fallbackStatusLabel(statusCode),
                                    value: snapshot.data().count,
                                    color: resolveMantineColor(ORDER_STATUS_TAGS[statusCode]?.badgeColor, theme, index),
                                };
                            }),
                        ))
                            .filter((slice) => slice.value > 0)
                            .sort((left, right) => {
                                if (right.value !== left.value) return right.value - left.value;
                                return left.label.localeCompare(right.label);
                            });

                        return {
                            fundId: fund.id,
                            nextExpectedNavDate,
                            slices,
                            total: slices.reduce((sum, slice) => sum + slice.value, 0),
                        } satisfies FundPeriodSummary;
                    }
                    catch (error) {
                        console.error("Failed to load open-ended order summary", fund.id, error);
                        return {
                            fundId: fund.id,
                            nextExpectedNavDate,
                            slices: [],
                            total: 0,
                            error: "Stats unavailable",
                        } satisfies FundPeriodSummary;
                    }
                }),
            );

            if (cancelled) {
                return;
            }

            setSummaries(nextSummaries.reduce<Record<string, FundPeriodSummary>>((accumulator, summary) => {
                accumulator[summary.fundId] = summary;
                return accumulator;
            }, {}));
            hasLoadedRef.current = true;
            setLoading(false);
        };

        fetchSummaries();

        return () => {
            cancelled = true;
        };
    }, [autoRefreshToken, db, funds, refreshToken, theme]);

    if (!funds.length) {
        return null;
    }

    return (
        <div
            style={{
                display: "grid",
                gridTemplateColumns: funds.length === 1 ? "minmax(0, 1fr)" : "repeat(auto-fit, minmax(180px, 1fr))",
                gap: 12,
                width: "100%",
                minWidth: 0,
            }}
        >
            {funds.map((fund) => {
                const summary = summaries[fund.id];
                const pieData = summary?.slices.map((slice) => ({
                    id: slice.code,
                    value: slice.value,
                    label: slice.label,
                })) || [];
                const colors = summary?.slices.map((slice) => slice.color) || [];
                const handleSliceSelect = (sliceCode: string) => {
                    if (!summary?.nextExpectedNavDate || !onSliceSelect) {
                        return;
                    }

                    onSliceSelect({
                        fundName: fund.name_en || fund.short_name || fund.id,
                        orderStatusCode: sliceCode,
                        expectedNavDate: format(summary.nextExpectedNavDate, "yyyy-MM-dd"),
                    });
                };
                const headingText = hideFundName ? (panelTitle || "Current period orders") : (fund.short_name || fund.name_en);

                return (
                    <Paper
                        key={fund.id}
                        p="xs"
                        radius="md"
                        withBorder
                        shadow="xs"
                        style={{ minHeight: cardMinHeight, minWidth: 0 }}
                    >
                        <Stack gap={4}>
                            <Group justify="space-between" gap={8} wrap="nowrap">
                                <Text size="sm" fw={700} lineClamp={1}>
                                    {headingText}
                                </Text>
                                {loading && <Loader size={12} />}
                            </Group>
                            <Text size="10px" c="dimmed">
                                {summary?.nextExpectedNavDate ? `Current batch ${format(summary.nextExpectedNavDate, "dd MMM")}` : "Current batch unavailable"}
                            </Text>
                            <Text size="xs" fw={600}>
                                {summary?.total || 0} order{summary?.total === 1 ? "" : "s"}
                            </Text>

                            {summary?.error ? (
                                <Center h={104}>
                                    <Text size="xs" c="red">
                                        {summary.error}
                                    </Text>
                                </Center>
                            ) : summary && summary.total > 0 ? (
                                <PieChart
                                    series={[
                                        {
                                            data: pieData,
                                            innerRadius: 20,
                                            outerRadius: 42,
                                            paddingAngle: 1,
                                        },
                                    ]}
                                    colors={colors}
                                    height={104}
                                    margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
                                    hideLegend
                                    onItemClick={onSliceSelect ? (_event, _identifier, item) => {
                                        if (typeof item.id === "undefined") {
                                            return;
                                        }
                                        handleSliceSelect(String(item.id));
                                    } : undefined}
                                    skipAnimation
                                    sx={{ "& .MuiPieArc-root": { cursor: onSliceSelect ? "pointer" : "default" } }}
                                />
                            ) : (
                                <Center h={104}>
                                    <Text size="xs" c="dimmed">
                                        No current-period orders
                                    </Text>
                                </Center>
                            )}

                            <Stack gap={2}>
                                {(summary?.slices || []).map((slice) => {
                                    const content = (
                                        <Group justify="space-between" gap={6} wrap="nowrap">
                                            <Group gap={6} wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                                                <span
                                                    style={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: "50%",
                                                        backgroundColor: slice.color,
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <Text size="10px" lineClamp={1}>
                                                    {slice.label}
                                                </Text>
                                            </Group>
                                            <Text size="10px" c="dimmed" fw={600}>
                                                {slice.value}
                                            </Text>
                                        </Group>
                                    );

                                    if (!onSliceSelect) {
                                        return (
                                            <div key={slice.code} style={{ width: "100%" }}>
                                                {content}
                                            </div>
                                        );
                                    }

                                    return (
                                        <UnstyledButton
                                            key={slice.code}
                                            type="button"
                                            onClick={() => handleSliceSelect(slice.code)}
                                            style={{ width: "100%", cursor: "pointer" }}
                                        >
                                            {content}
                                        </UnstyledButton>
                                    );
                                })}
                            </Stack>
                        </Stack>
                    </Paper>
                );
            })}
        </div>
    );
};

export const OpenEndedFundOrderPeriodSummary = ({
    fund,
    refreshToken = 0,
    panelTitle = "Current period orders",
    cardMinHeight = 234,
}: OpenEndedFundOrderPeriodSummaryProps) => {
    const funds = useMemo(() => [fund], [fund]);

    return (
        <OrderPeriodSummaryContent
            funds={funds}
            refreshToken={refreshToken}
            hideFundName
            panelTitle={panelTitle}
            cardMinHeight={cardMinHeight}
        />
    );
};

const OpenEndedOrderPeriodSummary = ({ refreshToken = 0, onSliceSelect }: OpenEndedOrderPeriodSummaryProps) => {
    const db = useFirestore();
    const fundsQuery = useMemo(
        () => query(collection(db, "funds"), where("active", "==", true), where("type", "==", "open_ended")),
        [db],
    );
    const { data: fundsData, loading: fundsLoading } = useFirestoreCollection(fundsQuery, true);
    const funds = useMemo(() => sortFundsByUpcomingNavDate((fundsData as Fund[]) || []), [fundsData]);

    if (fundsLoading && !funds.length) {
        return <Loader size="sm" />;
    }

    return <OrderPeriodSummaryContent funds={funds} refreshToken={refreshToken} onSliceSelect={onSliceSelect} />;
};

export default OpenEndedOrderPeriodSummary;
